import React from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const OPTIONS = [
  { id: 'auto', labelKey: 'settings.layoutAuto' },
  { id: 'mobile', labelKey: 'settings.layoutMobile', hintKey: 'settings.layoutMobileHint' },
  { id: 'desktop', labelKey: 'settings.layoutDesktop', hintKey: 'settings.layoutDesktopHint' },
];

/**
 * Which layout the game draws — a per-device preference, not a game setting,
 * so it never touches the socket.
 *
 * `Auto` follows the screen; the two overrides exist for the case the query
 * cannot see, most often a tablet whose owner disagrees with it.
 */
const useStyles = createUseStyles({
  plates: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
  },
  plate: {
    minHeight: '54px',
    padding: '10px 6px',
    background: '#3a2416',
    border: 'none',
    borderRadius: '7px',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#cbb493',
    textAlign: 'center',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 -2px 0 rgba(0,0,0,0.3)',
    ].join(','),
    transition: 'background .12s ease, color .12s ease',
    '&:hover': { background: '#472c1a', color: '#f0dcc0' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
  },
  plateActive: {
    background: '#5c3a1e',
    color: '#f5e6c8',
    boxShadow: [
      'inset 0 0 0 1.5px #e0a325',
      'inset 0 2px 5px rgba(0,0,0,0.45)',
    ].join(','),
    '&:hover': { background: '#5c3a1e', color: '#f5e6c8' },
  },
  hint: {
    display: 'block',
    marginTop: '3px',
    fontSize: '10px',
    fontWeight: 400,
    letterSpacing: '0.06em',
    color: '#8a6e4c',
    textTransform: 'none',
  },
  hintActive: {
    color: '#c4a070',
  },
});

function LayoutPicker({ pref, autoLayout, onChoose }) {
  const classes = useStyles();
  const t = useT();

  return (
    <div className={classes.plates}>
      {OPTIONS.map(({ id, labelKey, hintKey }) => {
        const active = pref === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            className={`${classes.plate} ${active ? classes.plateActive : ''}`}
            onClick={() => onChoose(id)}
          >
            {t(labelKey)}
            <span className={`${classes.hint} ${active ? classes.hintActive : ''}`}>
              {hintKey ? t(hintKey) : t('settings.layoutAutoHint', { layout: autoLayout })}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default LayoutPicker;
